import {THEME_LIST} from '@assets/const/themeSetting';
import {
  defaultWidgetSetting,
  PRECHAT_CUSTOMER_CHAT_AS
} from '@functions/const/defaultWidgetSetting';
import {getCustomPages} from '../widgetDisplayHelper';
import {CUSTOM_PAGES_FIELD} from '@functions/const/widgetDisplay';
import getDomain from './getDomain';
import convertToStringHtml from '../convertToStringHtml';
import presentText from '@functions/helpers/utils/presentText';
import merge from '@functions/helpers/utils/merge';
import {CONTACT_METHOD_HAS_PHONE} from '@assets/const/contactMethod';
import prepareEnableDisclaimerConsent from '@functions/helpers/disclaimerConsentHelper';

const prepareDefaultSettings = (shop, data = defaultWidgetSetting) => {
  const shopifyDomain = getDomain(shop);
  const isExistTheme = data.theme && THEME_LIST.includes(data.theme);
  const displayPages = data.displayPages || defaultWidgetSetting.displayPages;
  const newData = {
    headingTextFont: data.contentTextFont,
    timeZone: shop.shopInfo?.ianaTimezone,
    ...defaultWidgetSetting,
    ...data,
    disclaimerConsent: convertToStringHtml(
      presentText({text: data.disclaimerConsent, variables: {shopifyDomain}})
    ),
    shopName: data.shopName || shop.name,
    theme: isExistTheme ? data.theme : '',
    [CUSTOM_PAGES_FIELD]: getCustomPages(displayPages.values),
    contactMethod: merge(defaultWidgetSetting.contactMethod, data.contactMethod),
    inboxSettings: merge(defaultWidgetSetting.inboxSettings, data.inboxSettings),
    orderContactMethod:
      data.orderContactMethod ||
      prepareOrderContactMethod(merge(defaultWidgetSetting.contactMethod, data.contactMethod)),
    aiAssistantSettings: prepareAIAssistantSettings(data.aiAssistantSettings),
    newsletterPopupSettings: merge(defaultWidgetSetting.newsletterPopupSettings, {
      teaserMessage: data.newsletterPopupSettings?.message,
      invitationMessage: data.newsletterPopupSettings?.message,
      ...data.newsletterPopupSettings
    }),
    welcomePopupSetting: merge(defaultWidgetSetting.welcomePopupSetting, {
      teaserMessage: data.welcomePopupSetting?.message,
      welcomeMessage: data.welcomePopupSetting?.message,
      ...(data.welcomePopupSetting?.welcomeMessage && {
        onlineMessage: data.welcomePopupSetting?.welcomeMessage,
        offlineMessage: data.welcomePopupSetting?.welcomeMessage
      }),
      ...data.welcomePopupSetting
    }),
    preChatCustomerChatAs: (() => {
      if (data.hasOwnProperty('preChatCustomerChatAs')) {
        return data.preChatCustomerChatAs;
      }
      if (data.hasOwnProperty('isChatWithAnonymous')) {
        return data.isChatWithAnonymous
          ? PRECHAT_CUSTOMER_CHAT_AS.BOTH
          : PRECHAT_CUSTOMER_CHAT_AS.GUEST;
      }
      return defaultWidgetSetting.preChatCustomerChatAs;
    })(),
    isEnableDisclaimerConsent: prepareEnableDisclaimerConsent(data)
  };

  const [{value: defaultFont} = {}] = data.fontStore || [];
  if (!defaultFont) return removeUndefinedFields(newData);

  if (!newData.quickReplySettings.suggestionFAQs) {
    newData.quickReplySettings.suggestionFAQs = defaultWidgetSetting.quickReplySettings.suggestionFAQs.map(
      faq => ({...faq, answer: presentText({text: faq.answer, variables: {shopifyDomain}})})
    );
  }

  if (!newData.welcomePopupSetting.titleTextFont) {
    newData.welcomePopupSetting.titleTextFont = defaultFont;
  }
  if (!newData.welcomePopupSetting.messageTextFont) {
    newData.welcomePopupSetting.messageTextFont = defaultFont;
  }
  // to avoid migrate data
  if (newData.offlineStatus === 'few-minutes') {
    newData.offlineStatus = defaultWidgetSetting.offlineStatus;
  }
  // to migrate data require default email field pre chat form
  if (
    !newData.requiredEmailFieldPreChatForm &&
    !newData.requiredNameFieldPreChatForm &&
    !newData.requiredPhoneFieldPreChatForm
  ) {
    newData.requiredEmailFieldPreChatForm = true;
  }

  removePhoneNullContact(newData.contactMethod);

  return removeUndefinedFields(newData);
};

export function prepareOrderContactMethod(contactMethod) {
  const newArr = [];
  for (const [key, value] of Object.entries(contactMethod)) {
    if (!value.active) continue;
    const hasValue = CONTACT_METHOD_HAS_PHONE.includes(key) ? value.phone : value.value;
    if (hasValue) newArr.push(key);
  }
  return newArr;
}

export function prepareAIAssistantSettings(dataValue) {
  return merge(defaultWidgetSetting.aiAssistantSettings, dataValue);
}

function removePhoneNullContact(contactMethod) {
  Object.keys(contactMethod).forEach(key => {
    if (contactMethod[key].hasOwnProperty('phone') && contactMethod[key].phone === '') {
      delete contactMethod[key].phone; // Remove the phone key if it's an empty string
    }
  });
}

function removeUndefinedFields(obj) {
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedFields(item));
  }

  if (obj && typeof obj === 'object') {
    const newObj = {};
    Object.entries(obj).forEach(([key, value]) => {
      if (value !== undefined) {
        newObj[key] = removeUndefinedFields(value);
      }
    });
    return newObj;
  }

  return obj;
}

export default prepareDefaultSettings;
